import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, RefreshCw, Phone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, messageErreur } from "@/lib/api";
import { formatDateHeure } from "@/lib/format";
import { Vide } from "@/components/commun";
import { ChoixListe } from "@/pages/parametres/ChoixListe";

const COULEURS = { rappel: "var(--temp-3)", appel: "var(--accent)", rdv: "var(--temp-6)", visite: "var(--temp-7)", relance: "var(--temp-2)", document: "var(--muted)" };
const jourCle = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const debutJour = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const ajout = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const lundi = (d) => ajout(debutJour(d), -((d.getDay() + 6) % 7));

const periode = (mode, ref) => {
  if (mode === "jour") return [debutJour(ref), ajout(debutJour(ref), 1)];
  if (mode === "semaine") return [lundi(ref), ajout(lundi(ref), 7)];
  const d = new Date(ref.getFullYear(), ref.getMonth(), 1);
  return [lundi(d), ajout(lundi(new Date(ref.getFullYear(), ref.getMonth() + 1, 0)), 7)];
};

const Evenement = ({ e, equipe, peutAppeler }) => (
  <div className="at-card p-3 flex items-center gap-3" style={{ borderLeft: `4px solid ${COULEURS[e.type] || "var(--accent)"}`, opacity: e.fait ? 0.55 : 1 }} data-testid={`agenda-event-${e.id}`}>
    <p className="font-bold tabular-nums shrink-0 w-12">{new Date(e.echeance).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</p>
    <div className="min-w-0 flex-1">
      <p className="font-semibold truncate">{e.type_label} · {e.fiche_id ? <Link to={`/fiches/${e.fiche_id}`} className="underline" data-testid={`agenda-event-link-${e.id}`}>{e.titre}</Link> : e.titre}</p>
      <p className="text-xs at-muted truncate">{[equipe && e.assigne_nom, e.note, e.fait && "Fait"].filter(Boolean).join(" · ")}</p>
    </div>
    {e.prospect_id && peutAppeler ? <Link to={`/focus?prospect=${e.prospect_id}`} className="at-icon-btn shrink-0" aria-label={`Appeler ${e.titre}`} data-testid={`agenda-event-call-${e.id}`}><Phone size={15} /></Link>
      : e.telephone && <a href={`tel:${e.telephone.replace(/\s/g, "")}`} className="at-icon-btn shrink-0" aria-label={`Appeler ${e.titre}`} data-testid={`agenda-event-call-${e.id}`}><Phone size={15} /></a>}
  </div>
);

const ListeJours = ({ jours, parJour, equipe, peutAppeler, moi }) => (
  <div className="space-y-4">
    {jours.map((j) => (
      <section key={jourCle(j)} data-testid={`agenda-day-${jourCle(j)}`}>
        <p className="at-label mb-2">{j.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</p>
        {(parJour[jourCle(j)] || []).length === 0 ? <p className="text-sm at-muted">Rien de prévu.</p> : <div className="space-y-2">{parJour[jourCle(j)].map((e) => <Evenement key={e.id} e={e} equipe={equipe} peutAppeler={peutAppeler && e.assigne_a === moi} />)}</div>}
      </section>
    ))}
  </div>
);

const Mois = ({ debut, ref, parJour, ouvrir }) => (
  <div className="grid grid-cols-7 gap-1" data-testid="agenda-month-grid">
    {["L", "M", "M", "J", "V", "S", "D"].map((l, i) => <p key={i} className="text-center at-label">{l}</p>)}
    {Array.from({ length: 42 }, (_, i) => ajout(debut, i)).filter((d, i) => i < 35 || d.getMonth() === ref.getMonth()).map((d) => {
      const n = (parJour[jourCle(d)] || []).length;
      return (
        <button key={jourCle(d)} type="button" onClick={() => ouvrir(d)} className="min-h-[52px] at-soft p-1 flex flex-col items-center justify-start gap-1 min-w-0" style={{ opacity: d.getMonth() === ref.getMonth() ? 1 : 0.4, outline: jourCle(d) === jourCle(new Date()) ? "2px solid var(--accent)" : "none" }} data-testid={`agenda-month-day-${jourCle(d)}`}>
          <span className="text-sm font-semibold">{d.getDate()}</span>
          {n > 0 && <span className="text-[11px] font-bold px-1.5 at-pill" style={{ background: "var(--accent)", color: "var(--accent-ink)" }}>{n}</span>}
        </button>
      );
    })}
  </div>
);

export default function Agenda() {
  const { user } = useAuth();
  const direction = ["dirigeant", "responsable"].includes(user.role);
  const [mode, setMode] = useState("semaine");
  const [ref, setRef] = useState(new Date());
  const [qui, setQui] = useState("moi");
  const [d, setD] = useState(null);
  const [debut, fin] = useMemo(() => periode(mode, ref), [mode, ref]);
  const charger = useCallback(() => {
    const p = { debut: debut.toISOString(), fin: fin.toISOString(), ...(qui === "equipe" ? { equipe: true } : qui !== "moi" ? { user_id: qui } : {}) };
    api.get("/agenda", { params: p }).then((r) => setD(r.data)).catch((e) => toast.error(messageErreur(e)));
  }, [debut, fin, qui]);
  useEffect(() => { charger(); }, [charger]);
  const parJour = useMemo(() => (d?.evenements || []).reduce((acc, e) => { const k = jourCle(new Date(e.echeance)); (acc[k] = acc[k] || []).push(e); return acc; }, {}), [d]);
  const decaler = (s) => setRef(mode === "jour" ? ajout(ref, s) : mode === "semaine" ? ajout(ref, 7 * s) : new Date(ref.getFullYear(), ref.getMonth() + s, 1));
  const synchro = () => api.post("/agenda/synchroniser").then((r) => { toast.success(`Google Agenda synchronisé (simulation) : ${r.data.envoyes} événement(s) envoyé(s).`); charger(); }).catch((e) => toast.error(messageErreur(e)));
  const titre = mode === "mois" ? ref.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : mode === "jour" ? ref.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }) : `Semaine du ${debut.toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}`;
  const jours = mode === "jour" ? [debutJour(ref)] : Array.from({ length: 7 }, (_, i) => ajout(debut, i));

  return (
    <div className="min-w-0 space-y-4" data-testid="page-agenda">
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-testid="page-title">Agenda</h1>
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {[["jour", "Jour"], ["semaine", "Semaine"], ["mois", "Mois"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setMode(k)} className="min-h-[44px] px-4 at-pill font-semibold" style={{ background: mode === k ? "var(--accent)" : "var(--surface)", color: mode === k ? "var(--accent-ink)" : "var(--ink)" }} data-testid={`agenda-mode-${k}`}>{l}</button>
        ))}
      </div>
      {direction && d && (
        <ChoixListe label="Agenda de" valeur={qui} onChange={setQui} testId="agenda-who"
          options={[{ code: "moi", label: "Moi" }, { code: "equipe", label: "Toute l'équipe" }, ...d.equipe.filter((u) => u.id !== user.id).map((u) => ({ code: u.id, label: u.nom }))]} />
      )}
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => decaler(-1)} className="at-icon-btn shrink-0" aria-label="Période précédente" data-testid="agenda-prev"><ChevronLeft size={18} /></button>
        <p className="font-bold flex-1 text-center first-letter:uppercase truncate" data-testid="agenda-period">{titre}</p>
        <button type="button" onClick={() => decaler(1)} className="at-icon-btn shrink-0" aria-label="Période suivante" data-testid="agenda-next"><ChevronRight size={18} /></button>
        <button type="button" onClick={() => setRef(new Date())} className="at-btn at-btn-ghost shrink-0 !px-3 text-sm" data-testid="agenda-today">Aujourd'hui</button>
      </div>
      {!d ? <p className="at-muted" data-testid="agenda-loading">Chargement…</p> : (
        <>
          {mode === "mois" ? <Mois debut={debut} ref={ref} parJour={parJour} ouvrir={(j) => { setRef(j); setMode("jour"); }} /> : <ListeJours jours={jours} parJour={parJour} equipe={qui !== "moi"} peutAppeler={user.navigation.onglets.includes("appels")} moi={user.id} />}
          {mode === "mois" && d.evenements.length === 0 && <Vide texte="Aucun rappel, RDV ou visite ce mois-ci." testId="agenda-empty" />}
        </>
      )}
      <section className="at-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid="agenda-google">
        <div className="min-w-0"><p className="font-semibold">Google Agenda <span className="text-xs at-muted">(simulation)</span></p><p className="text-xs at-muted" data-testid="agenda-google-last">{d?.synchro_google ? `Dernière synchronisation : ${formatDateHeure(d.synchro_google)}` : "Jamais synchronisé"}</p></div>
        <button type="button" onClick={synchro} className="at-btn at-btn-ghost" data-testid="agenda-google-sync"><RefreshCw size={15} />Synchroniser</button>
      </section>
    </div>
  );
}
